<script setup lang="ts">
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';
import newConstructionImageSrc from '~/assets/images/property/hero-home-new-construction.jpg';
import businessImageSrc from '~/assets/images/property/hero-home-business.jpg';

const { t } = useI18n();
const route = useRoute();
const { propertySearchCriteria } = usePropertySearchCriteria(route);

// Content should appear ONLY when:
// • the right exact criteria are selected (business or new construction)
// • on any location (coming from home, user can select one)
// • on the first page
// • on sale only
// https://iad-holding.atlassian.net/browse/WEB-641
const excludedSearchCriteria: Array<keyof PropertySearchCriteria> = [
  'page',
  'itemsPerPage',
  'sort',
  'transactionType',
  'locations',
];
const remainingFiltersCount = computed<number>(() => {
  return Object.entries(propertySearchCriteria.value)
    .filter(([key]) => !excludedSearchCriteria.includes(key as keyof PropertySearchCriteria))
    .reduce((acc, [_, value]) => {
      if (!value) return acc;
      return acc + (Array.isArray(value) ? value.length : 1);
    }, 0);
});

const mightDisplayContent = computed(() => {
  const isHavingOnlyMinimalCriteria = remainingFiltersCount.value === 1;
  const isSale = propertySearchCriteria.value.transactionType === 'sale';
  const isFirstPage = propertySearchCriteria.value.page === 1;
  const haveFiltersRequirements = isHavingOnlyMinimalCriteria && isSale && isFirstPage;
  if (!haveFiltersRequirements) return false;

  const isBusiness = propertySearchCriteria.value.propertyTypes[0] === 'business';
  const isNewConstruction = propertySearchCriteria.value.projectTypes[0] === 'new';
  return isBusiness || isNewConstruction;
});

const editorialContent = computed(() => {
  if (!mightDisplayContent.value) return;
  const contentType = propertySearchCriteria.value.propertyTypes[0] === 'business' ? 'business' : 'new';
  return {
    title: t(`property_serp.header.${contentType}.title`),
    text: t(`property_serp.header.${contentType}.text`),
    alt: t(`property_serp.header.${contentType}.alt_text`),
    src: contentType === 'business' ? businessImageSrc : newConstructionImageSrc,
  };
});
</script>

<template>
  <div class="contents">
    <template v-if="editorialContent">
      <div class="grid lg:grid-cols-2 gap-l lg:gap-header lg:border-b lg:border-neutral-70 lg:pt-l lg:pb-2xl">
        <figure>
          <img
            loading="lazy"
            :src="editorialContent.src"
            class="object-cover lg:rounded-2xl w-full max-h-60 md:max-h-96 lg:max-h-80"
            :alt="editorialContent.alt"
          >
        </figure>
        <IadWrapper>
          <div class="flex flex-col gap-m lg:gap-l pb-2xl lg:pb-0 border-b border-neutral-70 lg:border-none">
            <IadTitle
              tag="h2"
              class="text-secondary"
              :text="editorialContent.title"
              :variant="'x-large'"
            />
            <p
              class="whitespace-pre-line text-lg lg:text-2xl pl-xs lg:pl-s"
              v-text="editorialContent.text"
            />
          </div>
        </IadWrapper>
      </div>
    </template>
  </div>
</template>
